import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRouteGuard } from './guards/useRouteGuard';
import type { RouteGuardKind } from './guards/types';
import RouteGuardSpinner from './RouteGuardSpinner';
import { getDefaultPublicRedirectPath } from '../shared/routeAccess';

export interface ProtectedRouteProps {
  guard: RouteGuardKind;
  children: React.ReactNode;
  /** Rendered when guard denies (e.g. admin login). */
  fallback?: React.ReactNode;
  /** Redirect when denied and no fallback (default: /new for internal). */
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  guard,
  children,
  fallback,
  redirectTo,
}) => {
  const location = useLocation();
  const [revision, setRevision] = useState(0);
  const status = useRouteGuard(guard, revision);

  const fallbackWithRetry =
    fallback && React.isValidElement(fallback)
      ? React.cloneElement(fallback as React.ReactElement<{ onAuthenticated?: () => void }>, {
          onAuthenticated: () => setRevision((r) => r + 1),
        })
      : fallback;

  if (status === 'loading') {
    return <RouteGuardSpinner />;
  }

  if (status === 'allowed') {
    return <>{children}</>;
  }

  if (fallbackWithRetry) {
    return <>{fallbackWithRetry}</>;
  }

  const target =
    redirectTo ??
    (guard === 'internal' ? getDefaultPublicRedirectPath() : '/dev/login');

  return <Navigate to={target} replace state={{ from: location }} />;
};

export default ProtectedRoute;
